<script>
    import { sgettext } from '../../i18n.js';
    import { createEventDispatcher,onMount } from 'svelte';
    import { fade } from 'svelte/transition';
    import {submit} from '../../utils.js';

    export let key = '';
    export let activityVersionId;
    export let media = null;
    export let url = null;
    export let readOnly = false;
    export let allowFileModifications = false;
    
    $: uploading = true;
    $: active = false;

    const dispatch = createEventDispatcher();

    function formatFileSize(fileSizeInBytes) {
        const units = ["kilobyte", "megabyte", "gigabyte"];
        const thresholds = [1024, 1024**2, 1024**3];

        let unitIndex = 0;
        while (fileSizeInBytes >= thresholds[unitIndex + 1] && unitIndex < units.length - 1) {
        unitIndex++;
        }

        const options = {
        style: "unit",
        unit: units[unitIndex],
        unitDisplay: "short",
        maximumFractionDigits: 2,
        };

        const formatter = new Intl.NumberFormat(undefined, options);
        return formatter.format(fileSizeInBytes / thresholds[unitIndex]);
    }

    function formatDate(dateString) {
        const date = new Date(dateString);
        
        const options = { day: '2-digit', month: '2-digit', year: '2-digit' };
        return date.toLocaleDateString('en-GB', options);
    }

    async function uploadAttachment(media) {
        if (media) {
            const formData = new FormData();
            const metadata = {
                chunkIndex: 1,
                totalChunks: 1,
                contentType: media.type,
                totalFileSize: media.size,
                fileName: media.name,
            };
            formData.append('files', media);
            formData.append('metadata', JSON.stringify(metadata));
            formData.append('activity_version_id',activityVersionId)
            media.error = null;
            
            const resp = await submit(`${url}upload/`, 'POST', formData, null, true);
            if (resp.ok) {
                const json = await resp.json();
                return json
            } else {
                media.error = {reason: sgettext('$$FAILED_UPLOAD_ATTACHMENT$$'), detail: sgettext('$$REQUEST_UPLOAD_ATTACHMENT_FAILED_UNKNOWN$$')};
            }
        }
    }

      async function createAttachment() {
        uploading=true;
        
        if(media.size > 100*(1024**2)) {
            media.error = { reason: sgettext('$$FILE_SIZE_EXCEEDS_100MB_LIMIT$$'), detail: sgettext('$$MAXIMUM_FILE_SIZE_UPLOADED_100MB$$') };
            uploading=false;
            return;
        }

        let upload_response = await uploadAttachment(media) 

        if (media.error){uploading=false;return;}

        media.location = upload_response.location

        let payload = {
            activity_version:activityVersionId,
            file_name:media.name,
            location:media.location,
            size:media.size,
        };

        const response = await submit(url, 'POST', payload);

        if (response.ok) {
            const json = await response.json();
            media.id = json.id
        } else {
            media.error = {reason: sgettext('$$FAILED_CREATE_ATTACHMENT$$'), detail: sgettext('$$REQUEST_CREATE_ATTACHMENT_FAILED_UNKNOWN$$')};
        }
        uploading=false;
    }
  
    async function removeFile() {
        if (media.id) {
            const response = await submit(`${url}expire/`, 'POST', {pk:media.id});
            if (!response.ok) {
                media.error ={reason: sgettext('$$FAILED_REMOVE_ATTACHMENT$$'), detail: sgettext('$$REQUEST_REMOVE_ATTACHMENT_FAILED_UNKNOWN$$')};
                return;
            }
        }
        dispatch('removeFile', media);
    }

    onMount(() => {
        if(media?.active) {
            uploading =false;
            active = true;
        } else {
            createAttachment(media);
        }
    });

</script>

<span
  class="pill {media?.error ? 'shake' : ''}"
  transition:fade={{ duration: 300 }}
>
  <div
    class="pill"
    style="background-color:{media?.error
      ? '#c44e47'
      : uploading
      ? '#595e68'
      : '#369763'}"
  >
    {#if media?.location && !uploading && !media?.error}
      <div class="file-download">
        <a
          class="filepond--file-action-button"
          title={sgettext('$$DOWNLOAD$$')}
          download={media.name}
          href={media.location}
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg
            aria-hidden="true"
            focusable="false"
            data-prefix="fas"
            data-icon="download"
            class="svg-inline--fa fa-download "
            role="img"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 512 512"
            width="14px"
            height="14px"
            ><path
              fill="currentColor"
              d="M288 32c0-17.7-14.3-32-32-32s-32 14.3-32 32V274.7l-73.4-73.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l128 128c12.5 12.5 32.8 12.5 45.3 0l128-128c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L288 274.7V32zM64 352c-35.3 0-64 28.7-64 64v32c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V416c0-35.3-28.7-64-64-64H346.5l-45.3 45.3c-25 25-65.5 25-90.5 0L165.5 352H64zM432 456c-13.3 0-24-10.7-24-24s10.7-24 24-24s24 10.7 24 24s-10.7 24-24 24z"
            /></svg
          >
        </a>
      </div>
    {/if}

    <div
      class="file-info"
      style="margin-left: {media?.location && !uploading && !media?.error
        ? ''
        : '15px'};"
    >
      <span
        class="item"
        title={media.name}
        style="font-size:.75em;font-weight:bold;">{media.name}</span
      >
      <span class="item" style="font-size:.625em;opacity:.75"
        >{formatFileSize(media.size)}</span
      >
    </div>
    <div class="file-status">
      <span class="item" style="font-size:.75em;font-weight:bold;">
        {#if media?.error}
          {sgettext('$$UPLOAD_FAILED$$')} -
          <span title={media?.error.detail} style="font-weight:normal;"
            >{media?.error.reason}</span
          >
        {:else if active}
          {sgettext('$$UPLOADED$$')}: {formatDate(media.created_date)}
        {:else}
          {uploading ? sgettext('$$UPLOADING$$') : sgettext('$$UPLOAD_COMPLETE$$')}
        {/if}
      </span>
      <span class="item" style="font-size:.625em;opacity:.75">
        {#if active && !allowFileModifications}
          {media.user_name}
        {:else}
          {uploading ? sgettext('$$TAP_CANCEL$$') : sgettext('$$TAP_REMOVE$$')}
        {/if}
      </span>
    </div>
    {#if !active || allowFileModifications}
      <div class="file-actions">
        <button
          class="filepond--file-action-button"
          type="button"
          data-align="left"
          on:click={removeFile}
        >
          {#if uploading && !media?.error}
            <div class="spinner" />
          {:else}
            <svg
              width="26"
              height="26"
              viewBox="0 0 26 26"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.586 13l-2.293 2.293a1 1 0 0 0 1.414 1.414L13 14.414l2.293 2.293a1 1 0 0 0 1.414-1.414L14.414 13l2.293-2.293a1 1 0 0 0-1.414-1.414L13 11.586l-2.293-2.293a1 1 0 0 0-1.414 1.414L11.586 13z"
                fill="currentColor"
                fill-rule="nonzero"
              />
            </svg>
          {/if}
        </button>
      </div>
    {/if}
  </div>
</span>

<style>
  .pill {
    display: flex;
    align-items: center;
    border-radius: 5px;
    justify-content: center;
    margin-bottom: 8px;
    height: 45px;
    opacity: 1;
    width: 95%;
    gap: 10px;
  }
  .pill .item {
    margin-right: 8px;
  }
  .filepond--file-action-button.filepond--file-action-button {
    font-size: 1em;
    width: 1.625em;
    height: 1.625em;
    font-family: inherit;
    line-height: inherit;
    padding: 0;
    border: none;
    outline: none;
    will-change: transform, opacity;
  }
  .filepond--file-action-button.filepond--file-action-button::after {
    position: absolute;
    left: -0.75em;
    right: -0.75em;
    top: -0.75em;
    bottom: -0.75em;
    content: '';
  }
  .filepond--file-action-button {
    cursor: auto;
    color: #fff;
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.5);
    background-image: none;
    box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
    transition: box-shadow 0.25s ease-in;
  }
  .filepond--file-action-button:hover,
  .filepond--file-action-button:focus {
    box-shadow: 0 0 0 0.125em rgba(255, 255, 255, 0.9);
  }
  .file-info {
    text-align: left;
    position: static;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    flex: 1;
    margin: 0 0.5em 0 0;
    min-width: 0;
    color: #fff;
  }

  .file-info .item {
    display: inline-block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    width: calc(90%);
  }
  .file-download {
    display: flex;
    flex-direction: column;
    margin-left: 10px;
  }

  .file-status {
    display: flex;
    align-items: flex-end;
    flex-direction: column;
    color: #fff;
  }

  .file-actions {
    display: flex;
    flex-direction: column;
    margin-right: 15px;
  }
  .spinner {
    width: 24px;
    height: 24px;
    border: 3px solid transparent;
    border-top-color: white;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
  .shake {
    animation: shake 0.3s;
  }

  @keyframes shake {
    0% {
      transform: translateX(0);
    }
    10%,
    30%,
    50%,
    70%,
    90% {
      transform: translateX(-10px);
    }
    20%,
    40%,
    60%,
    80% {
      transform: translateX(10px);
    }
    100% {
      transform: translateX(0);
    }
  }
</style>
